import { SingleSelectForm } from '../../single select form/single-select-form';
import { useAtom } from 'jotai';
import { dcaScenariosAtom, selectedDcaScenarioAtom } from '../atoms';
import { useEffect, useMemo } from 'react';
import { DcaScenariosResponse } from 'types/api-responses';
import style from './dca-scenario-selector.module.scss';
import { Link } from '@carbon/react';
import { startCase } from 'lodash-es';
import { parseMomentUtc } from 'utils/dates';
import { defaultDcaScenario } from '../utils';

export const DUMMY_DCA_SCENARIOS = [
  {
    id: 'dummy-1',
    name: 'Legacy PDP Default',
    createdAt: '',
    updatedAt: '',
    createdBy: 'System',
  },
  {
    id: 'dummy-2',
    name: 'Legacy Internal Reservoir',
    createdAt: '',
    updatedAt: '',
    createdBy: 'System',
  },
] as unknown as DcaScenariosResponse[];

interface DcaScenarioSelectorProps {
  formClassName?: string;
  /** Skip the initial fetch when scenario data is already loaded (e.g. from a parent page). */
  skipFetch?: boolean;
  /**
   * Controlled selection. Pass both to keep the choice local to the caller (the TC
   * DCA modal owns its own scenario) instead of writing the page-wide
   * `selectedDcaScenarioAtom`. Omit both for the page-level selector.
   */
  value?: DcaScenariosResponse | null;
  onChange?: (scenario: DcaScenariosResponse | null) => void;
}

/**
 * Dropdown for picking a DCA scenario. Unless `skipFetch` is set, it loads the
 * scenario list on mount and, when no scenario is selected yet, selects
 * "pdp default" (or the first scenario).
 *
 * @param {DcaScenarioSelectorProps} props - Fetch and selection options.
 * @return {JSX.Element} The dropdown.
 */
export const DcaScenarioSelector = ({
  formClassName,
  skipFetch = false,
  value,
  onChange,
}: DcaScenarioSelectorProps) => {
  const [dcaScenarios, fetchDcaScenarios] = useAtom(dcaScenariosAtom);
  const [pageDcaScenario, setPageDcaScenario] = useAtom(
    selectedDcaScenarioAtom
  );

  const isControlled = value !== undefined && onChange !== undefined;
  const selectedDcaScenario = isControlled ? value : pageDcaScenario;
  const handleChange = isControlled ? onChange : setPageDcaScenario;

  useEffect(() => {
    if (skipFetch) return;

    fetchDcaScenarios({
      url: '/queries/reservoir/dca_scenarios',
      type: 'POST',
      data: {
        query: {
          page: 1,
          pageSize: 50,
          sort_attribute: 'created_at',
          sort_direction: 'DESC',
        },
      },
      onSuccess: (data) => {
        const defaultScenario = defaultDcaScenario(data.results);
        if (defaultScenario) {
          setPageDcaScenario((current) => current ?? defaultScenario);
        }
      },
    });
  }, [fetchDcaScenarios, setPageDcaScenario, skipFetch]);

  const options = useMemo(() => {
    if (!dcaScenarios.result || dcaScenarios.result.type !== 'success')
      return [];

    const listed = [
      ...dcaScenarios.result.data.results,
      ...DUMMY_DCA_SCENARIOS,
    ];
    // A page can select a scenario older than the 50 newest listed (see
    // `useSeedDcaScenario`). List it first, so the selected one is shown.
    const isListed =
      !selectedDcaScenario ||
      listed.some((scenario) => scenario.id === selectedDcaScenario.id);
    return isListed ? listed : [selectedDcaScenario, ...listed];
  }, [dcaScenarios.result, selectedDcaScenario]);

  return (
    <SingleSelectForm
      id="dca-scenario-selector"
      options={options}
      value={selectedDcaScenario}
      isLoading={dcaScenarios.loading}
      onChange={handleChange}
      onSearch={(text, option) =>
        option.name.toLowerCase().includes(text.toLowerCase())
      }
      renderLabel={(scenario) => <DcaScenarioLabel scenario={scenario} />}
      formClassName={formClassName}
    />
  );
};

const DcaScenarioLabel = ({ scenario }: { scenario: DcaScenariosResponse }) => {
  const { createdDate, updatedDate } = useMemo(() => {
    return {
      createdDate: scenario.createdAt
        ? parseMomentUtc(scenario.createdAt).format('YYYY-MM-DD')
        : '--',
      updatedDate: scenario.updatedAt
        ? parseMomentUtc(scenario.updatedAt).format('YYYY-MM-DD')
        : '--',
    };
  }, [scenario.createdAt, scenario.updatedAt]);

  const scenarioName = useMemo(() => {
    const name = scenario.name;
    switch (name) {
      case 'pdp default':
        return 'PDP Default';

      case 'MLLE':
        return 'MLLE';

      default:
        return startCase(name);
    }
  }, [scenario.name]);

  const isDummy = useMemo(() => {
    return DUMMY_DCA_SCENARIOS.some((dummy) => dummy.id === scenario.id);
  }, [scenario.id]);

  return (
    <div className={style.scenarioLabel}>
      {isDummy ? (
        <div className={style.scenarioName}>{scenarioName}</div>
      ) : (
        <Link
          className={style.scenarioName}
          target="_blank"
          href={`/dca_scenarios/${scenario.id}`}
        >
          {scenarioName}
        </Link>
      )}
      <div className={style.scenarioCreator}>
        {scenario.createdBy || 'Unknown'}
      </div>
      <div className={style.scenarioDetails}>
        {`Created: ${createdDate} | Updated: ${updatedDate}`}
      </div>
    </div>
  );
};
